import type { SxProps, Theme } from '@mui/material';
import { createContext, useContext } from 'react';
import { createResizeDragHook } from '@client/hooks/use-resize-drag.hook';

/**
 * Shared resize state for the element list's left column. Every drag handle
 * in the list (header, footer, rows, filler, focus-view header) subscribes
 * to this single instance so they stay in lockstep on width.
 */
const elementListColumnResize = createResizeDragHook({
  storageKey: 'elementListLeftColumnWidth',
  min: 280,
  maxRatio: 0.6,
  defaultWidth: 320,
});

export const projectListColumnResize = createResizeDragHook({
  storageKey: 'projectListLeftColumnWidth',
  min: 280,
  maxRatio: 0.6,
  defaultWidth: 320,
});

const ColumnResizeContext = createContext(elementListColumnResize);
export const ListColumnResizeProvider = ColumnResizeContext.Provider;

export const useElementListColumnResize = () =>
  useContext(ColumnResizeContext).useResizeDrag();
export const useElementListColumnWidth = () =>
  useContext(ColumnResizeContext).useResizeWidth();
export const useElementListColumnResizeHandle = () =>
  useContext(ColumnResizeContext).useResizeHandle();

/**
 * CSS variable published by the list root (see {@link ELEMENT_LIST_WIDTH_VAR})
 * that descendants consume via `width: var(--element-list-col-width)` so
 * resizing doesn't force every row to re-render on every mousemove.
 */
export const ELEMENT_LIST_WIDTH_VAR = '--element-list-col-width';

/**
 * Ready-to-spread value for descendants that need the left column width.
 * Pair with {@link useElementListColumnResizeHandle} on the drag handle so
 * the consumer doesn't subscribe to width at all.
 */
export const ELEMENT_LIST_WIDTH_CSS = `var(${ELEMENT_LIST_WIDTH_VAR})`;

/**
 * Apply to every drag handle element so the wrapper's `:has()` selector can
 * light them all up when any one is hovered. Purely a CSS contract - no JS
 * listeners or re-renders involved.
 */
export const ELEMENT_LIST_RESIZE_HANDLE_CLASS = 'element-list-resize-handle';

/**
 * Spread onto the `sx` of a wrapper element that contains every
 * {@link ELEMENT_LIST_RESIZE_HANDLE_CLASS} handle for the column. When any
 * handle is hovered, all handles inside the wrapper light up together.
 */
export const elementListResizeHandleHoverSx: SxProps<Theme> = {
  [`&:has(.${ELEMENT_LIST_RESIZE_HANDLE_CLASS}:hover) .${ELEMENT_LIST_RESIZE_HANDLE_CLASS}`]:
    {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
};
